import React from "react";
import { isPrivateFilePath, openPrivateFile } from "../../../../utils/privateFile";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { SettlementAdjustment } from "../../../../services/SettlementService";
import { getAdjustmentReasonCodeInfo, getAdjustmentReasonCodeLabel } from "../../../../constants/adjustmentReasonCode";
import { formatNumber } from "../../../../services/SettlementService";

interface AdjustmentDetailModalProps {
	isOpen: boolean;
	adjustment: SettlementAdjustment | null;
	onClose: () => void;
	onApprove?: () => void;
	onReject?: () => void;
	currency?: string;
}

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 999999;
	padding: 20px;
`;

const ModalContent = styled.div`
	background: white;
	border-radius: 12px;
	width: 600px;
	max-width: calc(100vw - 40px);
	max-height: calc(100vh - 40px);
	overflow-y: auto;
	position: relative;
`;

const ModalHeader = styled.div`
	padding: 20px 20px 0 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ModalTitle = styled.h2`
	font-size: 20px;
	font-weight: 700;
	color: #111827;
	margin: 0;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 24px;
	color: #6b7280;
	cursor: pointer;
	padding: 4px;

	&:hover {
		color: #374151;
	}
`;

const ModalBody = styled.div`
	padding: 10px 20px;
`;

const InfoRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
	margin-bottom: 12px;

	&.full-width {
		grid-template-columns: 1fr;
	}
`;

const InfoBox = styled.div`
	border: 1px solid #e5e7eb;
	padding: 8px;
`;

const InfoLabel = styled.div`
	font-size: 12px;
	color: #6b7280;
	margin-bottom: 6px;
	font-weight: 500;
`;

const InfoValue = styled.div`
	font-size: 14px;
	color: #111827;
	font-weight: 500;
`;

const StatusTag = styled.div<{ backgroundColor: string; textColor: string }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background-color: ${(props) => props.backgroundColor};
	color: ${(props) => props.textColor};
	padding: 6px 12px;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 500;
	width: fit-content;
`;

const Dot = styled.div<{ color: string }>`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background-color: ${(props) => props.color};
`;

const FileItem = styled.div`
	display: flex;
	-webkit-box-align: center;
	align-items: center;
	-webkit-box-pack: justify;
	justify-content: space-between;
	background-color: #dbeafe;
	padding: 7px 16px;
	margin-bottom: 8px;
`;

const FileInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const FileName = styled.span`
	color: #1e40af;
	font-size: 14px;
	font-weight: 500;
`;

const DownloadButton = styled.button`
	background: none;
	border: none;
	color: #2563eb;
	font-size: 12px;
	cursor: pointer;
`;

const ModalFooter = styled.div`
	padding: 0 20px 20px 20px;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const ActionButton = styled.button<{ variant: "close" | "approve" | "reject" }>`
	padding: 10px 20px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	border: 1px solid;

	${(props) => {
		switch (props.variant) {
			case "close":
				return `
					background: white;
					color: #6B7280;
					border-color: #D1D5DB;
					
					&:hover {
						background: #F9FAFB;
					}
				`;
			case "approve":
				return `
					background: #10B981;
					color: white;
					border-color: #10B981;
					
					&:hover {
						background: #059669;
					}
				`;
			case "reject":
				return `
					background: #EF4444;
					color: white;
					border-color: #EF4444;
					
					&:hover {
						background: #DC2626;
					}
				`;
		}
	}}
`;

export default function AdjustmentDetailModal({
	isOpen,
	adjustment,
	onClose,
	onApprove,
	onReject,
	currency = "USD",
}: AdjustmentDetailModalProps) {
	if (!isOpen || !adjustment) return null;

	const formatTime = (dateString: string) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const year = String(date.getFullYear()).slice(-2);
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		return `${day}/${month}/${year} ${hours}:${minutes}`;
	};

	const getSourceInfo = () => {
		// Role: 1 = Admin, 2 = Partner, 3 = Customer
		if (adjustment.proposer?.role === 1) {
			return { name: "Whalelo", bg: "#DBEAFE", text: "#1E40AF", dot: "#1E40AF" };
		} else if (adjustment.proposer?.role === 2) {
			return { name: "Station", bg: "#ECFDF3", text: "#059669", dot: "#059669" };
		}
		return { name: "System", bg: "#FFEDD5", text: "#B45309", dot: "#B45309" };
	};

	const getTypeInfo = () => {
		if (adjustment.type === 10) {
			return { name: "Tăng chi phí", bg: "#ECFDF3", text: "#059669", dot: "#059669" };
		}
		return { name: "Giảm chi phí", bg: "#FEE2E2", text: "#DC2626", dot: "#DC2626" };
	};

	const getStatusInfo = () => {
		if (adjustment.status === 10) {
			return { name: "Chờ duyệt", bg: "#FEF3C7", text: "#B45309", dot: "#B45309" };
		} else if (adjustment.status === 20) {
			return { name: "Đã duyệt", bg: "#ECFDF3", text: "#059669", dot: "#059669" };
		} else if (adjustment.status === 30) {
			return { name: "Đã hủy", bg: "#FEE2E2", text: "#DC2626", dot: "#DC2626" };
		} else if (adjustment.status === 40) {
			return { name: "Đã từ chối", bg: "#FEE2E2", text: "#DC2626", dot: "#DC2626" };
		}
		return { name: "Không xác định", bg: "#E5E7EB", text: "#6B7280", dot: "#6B7280" };
	};

	const sourceInfo = getSourceInfo();
	const typeInfo = getTypeInfo();
	const statusInfo = getStatusInfo();
	const reasonInfo = adjustment.reasonCode ? getAdjustmentReasonCodeInfo(adjustment.reasonCode) : null;
	const adjustmentId = String(adjustment.id).padStart(6, "0");
	const valueColor = adjustment.type === 10 ? "#10B981" : "#EF4444";
	const valuePrefix = adjustment.type === 10 ? "+" : "-";
	const canApprove = adjustment.status === 10 && onApprove;
	const canReject = adjustment.status === 10 && onReject;

	const modalContent = (
		<ModalOverlay>
			<ModalContent>
				<ModalHeader>
					<ModalTitle>Chi tiết điều chỉnh #ADJ{adjustmentId}</ModalTitle>
					<CloseButton onClick={onClose}>×</CloseButton>
				</ModalHeader>

				<ModalBody>
					<InfoRow>
						<InfoBox>
							<InfoLabel>Thời gian tạo</InfoLabel>
							<InfoValue style={{ fontWeight: 600 }}>{formatTime(adjustment.createdAt)}</InfoValue>
						</InfoBox>
						<InfoBox>
							<InfoLabel>Nguồn tạo</InfoLabel>
							<StatusTag
								backgroundColor={sourceInfo.bg}
								textColor={sourceInfo.text}>
								<Dot color={sourceInfo.dot} />
								{sourceInfo.name}
							</StatusTag>
						</InfoBox>
					</InfoRow>

					<InfoRow>
						<InfoBox>
							<InfoLabel>Loại điều chỉnh</InfoLabel>
							<StatusTag
								backgroundColor={typeInfo.bg}
								textColor={typeInfo.text}
								style={{ fontWeight: 600 }}>
								<Dot color={typeInfo.dot} />
								{typeInfo.name}
							</StatusTag>
						</InfoBox>
						<InfoBox>
							<InfoLabel>Giá trị</InfoLabel>
							<InfoValue style={{ color: valueColor, fontWeight: 600 }}>
								{valuePrefix} {formatNumber(adjustment.amount)} {currency}
							</InfoValue>
						</InfoBox>
					</InfoRow>

					<InfoRow className="full-width">
						<InfoBox>
							<InfoLabel>Mã lý do</InfoLabel>
							<InfoValue style={{ fontWeight: "700", textTransform: "uppercase" }}>
								{adjustment.reasonCode}
								{reasonInfo && reasonInfo.description && (
									<span
										style={{
											fontWeight: "400",
											textTransform: "none",
											display: "block",
											marginTop: "4px",
											color: "#6B7280",
										}}>
										{reasonInfo.description}
									</span>
								)}
							</InfoValue>
						</InfoBox>
					</InfoRow>

					{adjustment.description && (
						<InfoRow className="full-width">
							<InfoBox>
								<InfoLabel>Mô tả chi tiết</InfoLabel>
								<InfoValue>{adjustment.description}</InfoValue>
							</InfoBox>
						</InfoRow>
					)}

					<InfoRow className="full-width">
						<InfoBox>
							<InfoLabel>Trạng thái</InfoLabel>
							<StatusTag
								backgroundColor={statusInfo.bg}
								textColor={statusInfo.text}>
								<Dot color={statusInfo.dot} />
								{statusInfo.name}
							</StatusTag>
						</InfoBox>
					</InfoRow>

					{adjustment.reviewer && (
						<InfoRow>
							<InfoBox>
								<InfoLabel>
									{adjustment.status === 20
										? "Người duyệt"
										: adjustment.status === 40
										? "Người từ chối"
										: "Người xử lý"}
								</InfoLabel>
								<InfoValue>
									{adjustment.reviewer.fullName || `User ${adjustment.reviewer.id}`}
									{adjustment.reviewer.role && (
										<span
											style={{
												fontSize: "12px",
												color: "#6B7280",
												display: "block",
												marginTop: "4px",
											}}>
											{adjustment.reviewer.role === 1
												? "Admin"
												: adjustment.reviewer.role === 2
												? "Station"
												: "System"}
										</span>
									)}
								</InfoValue>
							</InfoBox>
							{(adjustment.status === 40 || adjustment.status === 30) && (
								<InfoBox>
									<InfoLabel>{adjustment.status === 40 ? "Lý do từ chối" : "Lý do hủy"}</InfoLabel>
									<InfoValue>{adjustment.reasonRefusal || "N/A"}</InfoValue>
								</InfoBox>
							)}
						</InfoRow>
					)}

					{adjustment.attachments && adjustment.attachments.length > 0 && (
						<InfoRow className="full-width">
							<InfoBox>
								<InfoLabel>Tệp đính kèm</InfoLabel>
								<div>
									{adjustment.attachments.map((attachment, index) => {
										const getFileName = (url: string) => {
											try {
												const urlObj = new URL(url);
												const pathname = urlObj.pathname;
												const fileName =
													pathname.split("/").pop() || url.split("/").pop() || url;

												const lastDotIndex = fileName.lastIndexOf(".");
												const hasExtension =
													lastDotIndex > 0 && lastDotIndex < fileName.length - 1;

												if (hasExtension) {
													const nameWithoutExt = fileName.substring(0, lastDotIndex);
													const extension = fileName.substring(lastDotIndex);

													if (nameWithoutExt.length > 30) {
														return nameWithoutExt.substring(0, 27) + "..." + extension;
													}
													return fileName;
												} else {
													return fileName.length > 30
														? fileName.substring(0, 27) + "..."
														: fileName;
												}
											} catch {
												const fileName = url.split("/").pop() || url;

												const lastDotIndex = fileName.lastIndexOf(".");
												const hasExtension =
													lastDotIndex > 0 && lastDotIndex < fileName.length - 1;

												if (hasExtension) {
													const nameWithoutExt = fileName.substring(0, lastDotIndex);
													const extension = fileName.substring(lastDotIndex);

													if (nameWithoutExt.length > 30) {
														return nameWithoutExt.substring(0, 27) + "..." + extension;
													}
													return fileName;
												} else {
													return fileName.length > 30
														? fileName.substring(0, 27) + "..."
														: fileName;
												}
											}
										};
										const displayFileName = getFileName(attachment);

										// Security: only allow http/https URLs
										const isSafeUrl = /^https?:\/\//i.test(attachment) || isPrivateFilePath(attachment);

										return (
											<FileItem key={index}>
												<FileInfo>
													<span>📎</span>
													<FileName>{displayFileName}</FileName>
												</FileInfo>
												{isSafeUrl && (
													<DownloadButton
														onClick={() =>
															isPrivateFilePath(attachment)
																? void openPrivateFile(attachment)
																: window.open(attachment, "_blank", "noopener,noreferrer")
														}>
														Tải xuống
													</DownloadButton>
												)}
											</FileItem>
										);
									})}
								</div>
							</InfoBox>
						</InfoRow>
					)}
				</ModalBody>

				<ModalFooter>
					<ActionButton
						variant="close"
						onClick={onClose}>
						Đóng
					</ActionButton>
					{canApprove && (
						<ActionButton
							variant="approve"
							onClick={onApprove}>
							✓ Duyệt
						</ActionButton>
					)}
					{canReject && (
						<ActionButton
							variant="reject"
							onClick={onReject}>
							✕ Từ chối
						</ActionButton>
					)}
				</ModalFooter>
			</ModalContent>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}
